import { faBookMedical, faChevronDown, faChevronUp, faCircle, faReceipt, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { EXPLAIN_DDX, SCAN_MULTI } from "../../../../amplify/appsync/write";
import CopyToClipboardButton from "../../../assistant/CopyToClipboardButton";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { SectionDDXButton } from "./SectionDDXButton";
import { SectionDDXDetails } from "./SectionDDXDetails";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { isSectionUnread, processTextForCopy } from "../utils";
import { dashboardLLMCache } from "../cache";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../../../utils/time";

interface SectionDDXProps {
  user: any;
  tenant: any;
  className?: string;
  isLoadingDdx: boolean;
  ddx: any;
  ddxActive: any;
  setDdxActive: (value: any) => void;
  patientVisit: any;
  feedbacks: any;
  setFeedbacks: (value: any) => void;
  statuses: any;
  setRegenerateTarget: (value: any) => void;
  setShowContextMenu: (value: any) => void;
  handleShowScanMultiModal: () => void;
  unreadFileVersionMap?: any;
  latestFileVersions?: any;
  sectionToSetter?: any;
  handleFileVersionChange?: any;
}

export const SectionDDX = ({
  user,
  tenant,
  className = '',
  isLoadingDdx,
  ddx,
  ddxActive,
  setDdxActive,
  patientVisit,
  feedbacks,
  setFeedbacks,
  statuses,
  setRegenerateTarget,
  setShowContextMenu,
  handleShowScanMultiModal,
  unreadFileVersionMap = undefined,
  latestFileVersions = undefined,
  sectionToSetter = undefined,
  handleFileVersionChange = undefined,
}: SectionDDXProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_DDX]) {
            const version = latestFileVersions[EXPLAIN_DDX];

            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                setScanMulti(data);
            })
        }
    }, [latestFileVersions]);

    if (!isLoadingDdx && (!ddx || ddx["error"])) {
        return <></>
    }

    const getTextToCopy = (includeEvidence?: boolean) => {
        let anchorDate = new Date();
        if (latestFileVersions && latestFileVersions[EXPLAIN_DDX]) {
            anchorDate = new Date(latestFileVersions[EXPLAIN_DDX].version_date);
        }

        let text = `DDX as of ${getLocalDateStrFromDate(anchorDate)} ${getLocalTimeStrFromDate(anchorDate)}. ` + processTextForCopy(ddx.ddx_summary, anchorDate) + "\n";
        if (ddx && ddx.prioritized_diagnoses && includeEvidence) {
            ddx.prioritized_diagnoses.forEach((data: any, idx: number) => {
                text += "\n\n" + data.diagnosis + ": " + processTextForCopy(data.justification, anchorDate);
                if (data.evidence) {
                    data.evidence.forEach((evi: any) => {
                        text += "\nEvidence:\n" + "- Source: " + processTextForCopy(evi.source, anchorDate) + "\n- Details: " + processTextForCopy(evi.details, anchorDate);
                    });
                }
            });
        }
        return text;
    };

    if (!isLoadingDdx && (!ddx || ddx["error"])) {
        return (
            <GenerateModuleHeroV2 type={EXPLAIN_DDX} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} />
        )
    }

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingDdx} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faBookMedical} fixedWidth /></span><strong>Differential Diagnosis</strong> {isSectionUnread(EXPLAIN_DDX, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={8} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={"explain_ddx"} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                        <CopyToClipboardButton user={user} module={"ddx"} onClick={() => {navigator.clipboard.writeText(getTextToCopy())}} customStyling="dashboard-round-btn" overlayTriggerMessage="Copy DDX" />
                        <CopyToClipboardButton user={user} module={"ddx_with_evidence"} onClick={() => {navigator.clipboard.writeText(getTextToCopy(true))}} customStyling="dashboard-round-btn" overlayTriggerMessage="Copy DDX with Evidence" defaultIcon={faReceipt} />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_DDX); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"ddx"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        <Row className="mt-2">
                            <Col md={12} className="description-text">
                                {
                                    ddx ? (
                                        <LLMDashboardMarkdownRenderer content={ddx.ddx_summary} scanMulti={scanMulti} />
                                    ) : (
                                        <>No Data</>
                                    )
                                }
                            </Col>
                        </Row>
                        {
                            ddx && ddx.prioritized_diagnoses && (
                                <Row className="mt-4">
                                    <Col md={6}>
                                        {
                                            ddx.prioritized_diagnoses.map((data: string, idx: number) => (
                                                <SectionDDXButton idx={idx} active={ddxActive} setActive={setDdxActive} data={data} />
                                            ))
                                        }
                                    </Col>
                                    <Col md={6}>
                                        {
                                            scanMulti && ddx.prioritized_diagnoses[ddxActive] && (
                                                <SectionDDXDetails idx={ddxActive} data={ddx.prioritized_diagnoses[ddxActive]} scanMulti={scanMulti} />
                                            )
                                        }
                                    </Col>
                                </Row>
                            )
                        }
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
